/**
 * SKILL-STEER-01 — who can take a call (Seth 2026-09-23: "should we have
 * journeyman, technician, new hire — skill levels like that for techs … or go
 * by each type of equipment … how does pegasus know what types need more
 * experienced techs and how do we maintain that … without overcomplicating").
 *
 * Both, kept small:
 *   LEVEL — one per tech: 1 Maintenance Tech · 2 Service Tech · 3 Senior Tech.
 *     The names are the office's (org_setting `tech_levels`) — call them
 *     Journeyman or whatever the shop says.
 *   SPECIALTIES — the narrow things a level doesn't capture (skill_domain):
 *     Mini-Split, Boiler Service, Commercial, Water Heater, Magic Pak…
 *
 * What a call needs comes from three visible places, all edited on ONE page
 * (Settings → Skills & Levels):
 *   1. the call type — its lowest level and any specialty ("No Heat → Service
 *      Tech or higher"; "Boiler — No Heat → Boiler Service"),
 *   2. the equipment on file — "a call about a mini-split needs Mini-Split",
 *      and "rooftop units → offer Camron first",
 *   3. the customer — a confirmed "wants a senior tech", or a requirement
 *      that names a specialty ("Mini Split Tech" in the notes; asked first).
 * The booking grid offers the techs who fit, first choices first. Anyone can
 * still be picked by hand — a mismatch is flagged, never blocked (the
 * ServiceTitan pattern: skills on job types, qualified techs on top, alerts
 * not walls). Nothing here is hidden logic: every rule is a field on that page.
 */
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useOrgSettingJson } from "@/lib/orgSettings";
import {
  buildEquipmentView,
  type EquipmentRow,
  type EquipCategory,
} from "@/lib/equipment";
import { useCustomerEquipment, useAiEquipmentOverrides } from "@/features/customer-360/CustomerEquipment";
import { useEquipmentOverrides } from "@/features/customer-360/equipmentOverrides";
import { useInstalledEquipment } from "@/features/jobs/jobs";
import type { EquipmentCategoryRow } from "@/features/settings/equipmentCategories";

// ── Levels ──────────────────────────────────────────────────────────────────

export const DEFAULT_LEVEL_LABELS = ["Maintenance Tech", "Service Tech", "Senior Tech"];
export const LEVELS = [1, 2, 3] as const;

/** The office's names for levels 1–3 (index 0 = level 1). */
export function useLevelLabels(): string[] {
  const { data } = useOrgSettingJson<{ labels?: string[] }>("tech_levels", { labels: DEFAULT_LEVEL_LABELS });
  return useMemo(() => {
    const l = data?.labels;
    return LEVELS.map((n, i) => (Array.isArray(l) && typeof l[i] === "string" && l[i].trim()) || DEFAULT_LEVEL_LABELS[n - 1]);
  }, [data]);
}

/** "Service Tech or higher" / "Senior Tech". */
export function levelNeedText(labels: string[], min: number): string {
  const name = labels[min - 1] ?? `Level ${min}`;
  return min < LEVELS.length ? `${name} or higher` : name;
}

// ── Who has what ────────────────────────────────────────────────────────────

export interface Specialty {
  id: number;
  name: string;
  description: string | null;
  qualifier: string | null;
  active: boolean;
  sort: number;
}

export function useSpecialties() {
  return useQuery({
    queryKey: ["skill_domains_all"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Specialty[]> => {
      const { data, error } = await supabase
        .from("skill_domain")
        .select("id, name, description, qualifier, is_active, sort_order")
        .order("sort_order")
        .order("id");
      if (error) throw error;
      return ((data ?? []) as { id: number; name: string; description: string | null; qualifier: string | null; is_active: boolean; sort_order: number | null }[])
        .map((d) => ({ id: d.id, name: d.name, description: d.description, qualifier: d.qualifier, active: d.is_active, sort: d.sort_order ?? 0 }));
    },
  });
}

export interface TechQual {
  /** 1–3, or null when nobody has set it yet. */
  level: number | null;
  /** skill_domain ids the office marked him capable of. */
  skills: Set<number>;
}

export function useTechQuals() {
  return useQuery({
    queryKey: ["tech_quals"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Map<string, TechQual>> => {
      const [p, s] = await Promise.all([
        supabase.from("tech_profile").select("empno, level"),
        supabase.from("tech_skill").select("empno, skill_domain_id").eq("admin_flagged_capable", true),
      ]);
      if (p.error) throw p.error;
      if (s.error) throw s.error;
      const m = new Map<string, TechQual>();
      const get = (e: string) => {
        let q = m.get(e);
        if (!q) { q = { level: null, skills: new Set() }; m.set(e, q); }
        return q;
      };
      for (const r of (p.data ?? []) as { empno: string; level: number | null }[]) get(r.empno).level = r.level ?? null;
      for (const r of (s.data ?? []) as { empno: string; skill_domain_id: number }[]) get(r.empno).skills.add(r.skill_domain_id);
      return m;
    },
  });
}

/** Why a tech doesn't fit — empty when he does. A tech with no level set
 *  doesn't pass a level bar: a new hire nobody has placed yet shouldn't look
 *  qualified for a No Heat (the Settings page flags him). */
export function missReasons(
  q: TechQual | undefined,
  minLevel: number | null,
  skillIds: number[],
  labels: string[],
  specName: (id: number) => string,
): string[] {
  const out: string[] = [];
  if (minLevel) {
    const want = levelNeedText(labels, minLevel);
    if (q?.level == null) out.push(`has no level set yet (this call wants ${want})`);
    else if (q.level < minLevel) out.push(`is a ${labels[q.level - 1] ?? `level ${q.level}`} (this call wants ${want})`);
  }
  const missing = skillIds.filter((id) => !q?.skills.has(id));
  if (missing.length) out.push(`isn't marked for ${missing.map(specName).join(" or ")}`);
  return out;
}

// ── What a call is about ────────────────────────────────────────────────────

export type CallSystem = "heat" | "cool" | "water";

/** Which systems each kind of unit serves. Admin-added "main system" kinds
 *  count for heating and cooling. */
const EQUIP_SYSTEMS: Partial<Record<string, CallSystem[]>> = {
  furnace: ["heat"], boiler: ["heat"], "garage-heater": ["heat"],
  ac: ["cool"], coil: ["cool"],
  "heat-pump": ["heat", "cool"], "mini-split": ["heat", "cool"], "magic-pak": ["heat", "cool"],
  rooftop: ["heat", "cool"], "air-handler": ["heat", "cool"],
  "water-heater": ["water"],
};

/** Tags the "About" chips in Settings → Service Types write. Once a type
 *  carries any of them, the admin's answer is the whole answer. */
const ABOUT_TAGS = new Set(["heat", "cool", "water", "none"]);

/** The systems a call type is about. The admin sets this per type in
 *  Settings → Service Types ("About: Heating / Cooling / Water heater");
 *  a type nobody has set yet is read from its name and older tags —
 *  installs, estimates, plumbing and add-on work (humidifiers, ducts) aren't
 *  about the unit on file, so the equipment doesn't steer them. */
export function systemsForType(t: { name: string; category: string; skills: string[] | null } | null): CallSystem[] {
  if (!t) return [];
  const tags = new Set((t.skills ?? []).map((x) => x.toLowerCase()));
  if ([...tags].some((x) => ABOUT_TAGS.has(x))) {
    return (["heat", "cool", "water"] as CallSystem[]).filter((k) => tags.has(k));
  }
  if (t.category === "install" || t.category === "sales") return [];
  const n = t.name.toLowerCase();
  if (/humidif|air quality|dryer vent|\bduct/.test(n)) return [];
  const out = new Set<CallSystem>();
  if (/water heater|hot water/.test(n)) out.add("water");
  if (tags.has("furnace") || tags.has("boiler") || /\bheat\b|furnace|boiler/.test(n)) out.add("heat");
  if (tags.has("ac") || /\bcool|\bac\b|a\/c|air cond|\bcoil/.test(n)) out.add("cool");
  if (out.size === 0 && !tags.has("plumbing")) { out.add("heat"); out.add("cool"); }
  return [...out];
}

export interface EquipNeed {
  /** equipment category key ("mini-split"). */
  key: string;
  label: string;
  skillId: number | null;
  offerFirst: string[];
  units: number;
  /** The only kind of unit on file for this call's systems — the call is
   *  clearly about it, so it applies on its own (the dispatcher can ✕ it).
   *  Otherwise it's asked ("this home also has a Boiler — about it?"). */
  auto: boolean;
}

/** The specialty kinds of equipment this call could be about. */
export function equipmentNeeds(
  systems: CallSystem[],
  units: { category: string }[],
  cats: EquipmentCategoryRow[],
): EquipNeed[] {
  if (systems.length === 0 || units.length === 0) return [];
  const catBy = new Map(cats.filter((c) => c.active).map((c) => [c.key, c]));
  const counts = new Map<string, number>();
  for (const u of units) {
    const sys = EQUIP_SYSTEMS[u.category] ?? (catBy.get(u.category)?.grouping === "system" ? (["heat", "cool"] as CallSystem[]) : []);
    if (!sys.some((s) => systems.includes(s))) continue;
    counts.set(u.category, (counts.get(u.category) ?? 0) + 1);
  }
  const kinds = [...counts.keys()];
  const out: EquipNeed[] = [];
  for (const k of kinds) {
    const c = catBy.get(k);
    if (!c) continue;
    const offer = c.offer_first ?? [];
    if (c.needs_skill_id == null && offer.length === 0) continue;
    out.push({ key: k, label: c.label, skillId: c.needs_skill_id, offerFirst: offer, units: counts.get(k) ?? 0, auto: kinds.length === 1 });
  }
  return out;
}

/** The customer's working units (replaced ones and duplicates out, the
 *  office's corrections in, units added in Pegasus included), classified the
 *  same way the Equipment tab does. */
export function useCallEquipment(custno: string | null, locno: string | null) {
  const loc = locno ?? "00001";
  const { data: rows } = useCustomerEquipment(custno ?? undefined, loc);
  const { data: overrides } = useEquipmentOverrides(custno ?? undefined, loc);
  const { data: installed } = useInstalledEquipment(custno, loc);
  const ai = useAiEquipmentOverrides(rows ?? []).data;
  return useMemo(() => {
    if (!rows) return null;
    const merged: EquipmentRow[] = rows.map((r) => {
      const ov = overrides?.get(r.counter);
      return ov ? { ...r, mfg: ov.mfg ?? r.mfg, model: ov.model ?? r.model, serial: ov.serial ?? r.serial, install_date: ov.install_date ?? r.install_date, eq_type: ov.eq_type ?? r.eq_type } : r;
    });
    for (const p of installed ?? []) {
      merged.push({
        counter: `peg:${p.id}`, mfg: p.brand, model: p.model, serial: p.serial, eq_type: p.category,
        equip_loc: null, install_date: p.install_date, warranty_date: null, ser_agr_no: null,
        notes: null, custom_1: null, custom_2: null, invoice: null,
      });
    }
    const view = buildEquipmentView(merged, ai as Record<string, EquipCategory> | undefined, overrides);
    return view.systems.map((u) => ({ category: u.category as string, label: u.categoryLabel, model: u.model }));
  }, [rows, overrides, installed, ai]);
}

// ── Settings writes ─────────────────────────────────────────────────────────

const whoami = async () => (await supabase.auth.getUser()).data.user?.email ?? null;

/** Everything that reads skills or levels, refreshed together. */
function useRefreshSkills() {
  const qc = useQueryClient();
  return () => Promise.all([
    qc.invalidateQueries({ queryKey: ["tech_quals"] }),
    qc.invalidateQueries({ queryKey: ["skill_domains_all"] }),
    qc.invalidateQueries({ queryKey: ["skill_domains"] }),
    qc.invalidateQueries({ queryKey: ["tech_skills"] }),
    qc.invalidateQueries({ queryKey: ["tech_skills_profile"] }),
    qc.invalidateQueries({ queryKey: ["tech_seniority"] }),
  ]);
}

export function useSetTechLevel() {
  const refresh = useRefreshSkills();
  return useMutation({
    mutationFn: async (v: { empno: string; level: number | null }) => {
      const { error } = await supabase.from("tech_profile").upsert(
        { empno: v.empno, level: v.level, updated_by: await whoami(), updated_at: new Date().toISOString() },
        { onConflict: "empno" },
      );
      if (error) throw error;
    },
    onSettled: () => refresh(),
  });
}

export function useSetTechSkill() {
  const refresh = useRefreshSkills();
  return useMutation({
    mutationFn: async (v: { empno: string; skillId: number; on: boolean }) => {
      if (v.on) {
        const { error } = await supabase.from("tech_skill").upsert(
          { empno: v.empno, skill_domain_id: v.skillId, admin_flagged_capable: true, last_admin_updated_at: new Date().toISOString(), last_admin_updated_by: (await whoami()) ?? "unknown" },
          { onConflict: "empno,skill_domain_id" },
        );
        if (error) throw error;
      } else {
        const { error } = await supabase.from("tech_skill").delete().eq("empno", v.empno).eq("skill_domain_id", v.skillId);
        if (error) throw error;
      }
    },
    onSettled: () => refresh(),
  });
}

export function useSaveSpecialty() {
  const refresh = useRefreshSkills();
  return useMutation({
    mutationFn: async (v: { id?: number; name?: string; active?: boolean; sort?: number }) => {
      const name = v.name?.trim();
      if (v.id == null) {
        if (!name) throw new Error("Name the specialty first");
        const { error } = await supabase.from("skill_domain").insert({ name, sort_order: v.sort ?? 1000, is_active: true });
        if (error) throw new Error(error.message.includes("duplicate") ? "That specialty already exists" : error.message);
        return;
      }
      const patch: Record<string, unknown> = { updated_at: new Date().toISOString() };
      if (name !== undefined) { if (!name) throw new Error("A specialty needs a name"); patch.name = name; }
      if (v.active !== undefined) patch.is_active = v.active;
      const { error } = await supabase.from("skill_domain").update(patch).eq("id", v.id);
      if (error) throw error;
    },
    onSettled: () => refresh(),
  });
}

export function useSaveTypeNeeds() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; min_level?: number | null; needs_skills?: number[]; offer_first?: string[] }) => {
      const { id, ...patch } = v;
      const { error } = await supabase.from("service_type").update({ ...patch, updated_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["service_types"] }),
  });
}
